/**
 * data-viewer のフィルタ条件を扱う純関数群。
 *
 * 絞り込みそのものは API 側が SQL の `WHERE` 句として実行する。以前はここで実行結果の行配列を
 * ブラウザ側で filter していたが、表示上限（200 行）の外にある行を絞り込めなかったため
 * サーバー側へ移した。ここに残るのは「UI が持つ条件を API 送信形へ変換する」ことと、
 * 登録画面がカラムの型を実データから推論することだけ。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business data-viewer のフィルタ条件を API 送信形へ変換し、クエリ登録時のカラム型推論を提供する
 * @graph-connects none
 */
import type { FilterConditionInput, RawFilterConditionInput } from '../types/api.js';

/** フィルタ条件の値型。 */
export type FilterType = 'date' | 'number' | 'string';

/**
 * UI が保持するフィルタ条件1件。
 *
 * `id` は React の key と更新対象の特定にだけ使い、API へは送らない。
 * `values` は入力途中の状態をそのまま保てるよう **カンマ区切りの生文字列** で持ち、
 * 送信時に {@link parseFilterValues} で配列へ分解する（配列で保持すると
 * 「1001, 」まで打った時点で末尾のカンマが消えて次の値を入力できない）。
 */
export interface FilterCondition {
  /** UI 上でのキー（React key・更新対象特定に使用） */
  id: string;
  /**
   * 対象テーブル名。登録画面で DB 内カラムから選んだ条件だけが持つ。
   *
   * ある場合は集約前の生データへ（同名 CTE を前置して元 SQL の参照を差し替える）、
   * 無い場合は実行結果へ（派生テーブルの外側 `WHERE`）条件がかかる。
   */
  table?: string;
  /** 対象カラム名 */
  column: string;
  /** フィルタ種別 */
  type: FilterType;
  /** 値の列挙（カンマ区切りの生入力）。SQL の `IN` 句になる */
  values?: string;
  /** date: 開始日（inclusive） */
  dateFrom?: string;
  /** date: 終了日（inclusive） */
  dateTo?: string;
  /** number: 最小値（inclusive） */
  numberMin?: string;
  /** number: 最大値（inclusive） */
  numberMax?: string;
}

/** 型判定に使う非null値サンプルの最大件数。 @graph-connects none */
const MAX_TYPE_SAMPLE_SIZE = 50;

/** date-only 文字列（`YYYY-MM-DD`）にマッチする正規表現。 @graph-connects none */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * セル値を表示・比較用の文字列に変換する。
 * data-viewer.tsx のテーブル表示・CSVエクスポートと同一ロジックを共有する。
 *
 * @graph-connects none
 */
export function cellToString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/**
 * 日付文字列をローカルタイム基準でパースし epoch ms を返す。
 *
 * date-only 形式（`YYYY-MM-DD`）を素の `new Date()` に渡すと UTC 深夜 0 時として解釈されるが、
 * TZ 情報を持たない datetime セル値（`new Date()` がローカルタイムとして解釈する）と
 * 一貫させるため、date-only 文字列はローカル深夜 0 時として明示的にパースする。
 *
 * @graph-connects none
 */
function parseLocalDateTime(text: string): number {
  const normalized = DATE_ONLY_PATTERN.test(text) ? `${text}T00:00:00` : text;
  return new Date(normalized).getTime();
}

/**
 * 値が日付としてパース可能かどうかを判定する。
 *
 * @graph-connects none
 */
function isParsableAsDate(value: unknown): boolean {
  const text = cellToString(value);
  if (text === '') return true;
  return !Number.isNaN(parseLocalDateTime(text));
}

/**
 * 値が数値としてパース可能かどうかを判定する。
 *
 * @graph-connects none
 */
function isParsableAsNumber(value: unknown): boolean {
  const text = cellToString(value);
  if (text === '') return true;
  return !Number.isNaN(Number(text));
}

/**
 * カラムのデータ型を実データから推論する。
 *
 * 対象カラムの非null値サンプル（先頭から最大 {@link MAX_TYPE_SAMPLE_SIZE} 個）を見て、
 * 全て数値としてパース可能なら `'number'`、（数値ではなく）全て日付としてパース可能なら
 * `'date'`、それ以外またはサンプル0件なら `'string'` を返す。
 *
 * 判定順は **number → date → string** に固定する。"123" のような数値文字列は
 * `new Date()` にも解釈されてしまうため、number 判定を先に行わないと数値カラムが
 * date と誤判定される事故が起きる。
 *
 * @graph-connects none
 */
export function inferFilterType(rows: Record<string, unknown>[], column: string): FilterType {
  const sampleValues: unknown[] = [];
  for (const row of rows) {
    const value = row[column];
    if (value === null || value === undefined) continue;
    sampleValues.push(value);
    if (sampleValues.length >= MAX_TYPE_SAMPLE_SIZE) break;
  }

  if (sampleValues.length === 0) return 'string';
  if (sampleValues.every((value) => isParsableAsNumber(value))) return 'number';
  if (sampleValues.every((value) => isParsableAsDate(value))) return 'date';
  return 'string';
}

/**
 * カンマ区切りの入力を値の配列へ分解する。
 * 前後の空白を落とし、空要素は捨てる（`1001, 1005,` のような末尾カンマを許容するため）。
 *
 * @graph-connects none
 */
export function parseFilterValues(raw: string): string[] {
  return raw
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value !== '');
}

/**
 * UI のフィルタ条件を API 送信形へ変換する。
 *
 * 値が1つも入っていない条件は送らない（API 側でも句を生成しないが、送信内容を
 * 「実際に効く条件だけ」に保つ）。`id` は UI 専用なので落とす。
 *
 * @param conditions UI が保持する条件
 * @returns API へ送るフィルタ条件。効く条件が無ければ空配列
 * @graph-connects none
 */
export function toFilterConditionInputs(
  conditions: readonly FilterCondition[],
): FilterConditionInput[] {
  const inputs: FilterConditionInput[] = [];

  for (const condition of conditions) {
    const values = parseFilterValues(condition.values ?? '');
    const hasAnyValue =
      values.length > 0 ||
      Boolean(condition.dateFrom) ||
      Boolean(condition.dateTo) ||
      Boolean(condition.numberMin) ||
      Boolean(condition.numberMax);
    if (!hasAnyValue) continue;

    inputs.push({
      column: condition.column,
      type: condition.type,
      ...(values.length > 0 ? { values } : {}),
      ...(condition.dateFrom ? { dateFrom: condition.dateFrom } : {}),
      ...(condition.dateTo ? { dateTo: condition.dateTo } : {}),
      ...(condition.numberMin ? { numberMin: condition.numberMin } : {}),
      ...(condition.numberMax ? { numberMax: condition.numberMax } : {}),
    });
  }

  return inputs;
}

/**
 * UI のフィルタ条件を、実行結果向け（`filters`）と生データ向け（`rawFilters`）に振り分ける。
 *
 * 振り分けの基準は条件が `table` を持つかどうか。登録画面で DB 内カラムから選んだ条件だけが
 * `table` を持ち、そちらは集約前の行に条件がかかる。値が 1 つも入っていない条件は
 * どちらにも入れない。
 *
 * @param conditions UI が保持する条件
 * @returns 実行 API へ渡す 2 種類のフィルタ条件
 * @graph-connects none
 */
export function splitFilterConditionInputs(conditions: readonly FilterCondition[]): {
  filters: FilterConditionInput[];
  rawFilters: RawFilterConditionInput[];
} {
  const filters: FilterConditionInput[] = [];
  const rawFilters: RawFilterConditionInput[] = [];

  // 1 件ずつ変換する。まとめて渡すと値が空の条件が落ちて添字が元の配列とずれ、
  // 別の条件のテーブル名を貼ってしまう
  for (const condition of conditions) {
    const [converted] = toFilterConditionInputs([condition]);
    if (converted === undefined) continue;

    const table = condition.table;
    if (table === undefined || table === '') {
      filters.push(converted);
      continue;
    }
    rawFilters.push({ ...converted, table });
  }

  return { filters, rawFilters };
}
